import { useEffect, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { HiXMark, HiCalendar, HiClock, HiMicrophone, HiMusicalNote } from 'react-icons/hi2';
import { parseEpisodeTitle, formatDateTime } from '../../utils/time';
import type { Episode, Show, Stream } from '../../types';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  episode: Episode;
  show: Show;
  stream: Stream;
  presenter?: string;
  duration?: number;
}

export function InfoModal({
  isOpen,
  onClose,
  episode,
  show,
  stream,
  presenter,
  duration,
}: InfoModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Build fallback image chain: episodeimage -> episode.image -> stream.image
  const imageFallbacks = [
    episode.episodeimage,
    episode.image,
    stream.image,
  ].filter(Boolean) as string[];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const currentImage = imageFallbacks[currentImageIndex] || stream.image;

  // Reset image index when episode changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [episode.episodeid]);

  // Handle image load error - try next fallback
  const handleImageError = () => {
    if (currentImageIndex < imageFallbacks.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  // Handle animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const { name: episodeName, datetime: episodeDatetime } = parseEpisodeTitle(
    episode.episodetitle
  );

  const formattedDate = episodeDatetime
    ? formatDateTime(episodeDatetime, stream.time_string_format)
    : null;

  // Format duration as HH:MM:SS or MM:SS
  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center
                  transition-colors duration-200
                  ${isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-modal-title"
    >
      <FocusTrap focusTrapOptions={{ initialFocus: false, allowOutsideClick: true }}>
      <div
        className={`bg-[var(--color-surface)] rounded-t-[var(--radius-2xl)] sm:rounded-[var(--radius-2xl)]
                   shadow-[var(--shadow-xl)] w-full sm:max-w-sm max-h-[80vh] overflow-hidden
                   border border-[var(--color-border)]
                   flex flex-col
                   transition-all duration-300 ease-out
                   ${isVisible
                     ? 'translate-y-0 opacity-100 scale-100'
                     : 'translate-y-8 opacity-0 scale-95'
                   }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with image */}
        <div className="relative flex-shrink-0">
          <div className="h-32 overflow-hidden">
            <img
              src={currentImage}
              alt={episodeName}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-surface)] via-transparent to-transparent" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-2 right-2 p-1.5
                       text-white/80 hover:text-white
                       bg-black/30 hover:bg-black/50
                       backdrop-blur-sm
                       transition-colors rounded-full
                       focus:outline-none focus-visible:ring-2
                       focus-visible:ring-white"
          >
            <HiXMark size={18} />
          </button>

          {/* Station badge */}
          <div className="absolute bottom-2 left-3">
            <span
              className="px-2 py-0.5 text-[10px] font-semibold text-white
                         rounded-full backdrop-blur-sm"
              style={{ backgroundColor: stream.color || 'var(--color-primary)' }}
            >
              {stream.streamname}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Title */}
          <h2
            id="info-modal-title"
            className="text-base font-semibold text-[var(--color-text)] leading-tight"
          >
            {episodeName}
          </h2>

          {/* Show title if different from episode */}
          {show.title !== episodeName && (
            <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
              {show.title}
            </p>
          )}

          {/* Metadata grid */}
          <div className="mt-4 space-y-2.5">
            {presenter && (
              <div className="flex items-center gap-2.5 text-sm">
                <HiMicrophone
                  size={16}
                  className="text-[var(--color-text-muted)] flex-shrink-0"
                />
                <span className="text-[var(--color-text-secondary)]">
                  {presenter}
                </span>
              </div>
            )}

            {formattedDate && (
              <div className="flex items-center gap-2.5 text-sm">
                <HiCalendar
                  size={16}
                  className="text-[var(--color-text-muted)] flex-shrink-0"
                />
                <span className="text-[var(--color-text-secondary)]">
                  {formattedDate}
                </span>
              </div>
            )}

            {duration && duration > 0 && (
              <div className="flex items-center gap-2.5 text-sm">
                <HiClock
                  size={16}
                  className="text-[var(--color-text-muted)] flex-shrink-0"
                />
                <span className="text-[var(--color-text-secondary)]">
                  {formatDuration(duration)}
                </span>
              </div>
            )}

            <div className="flex items-center gap-2.5 text-sm">
              <HiMusicalNote
                size={16}
                className="text-[var(--color-text-muted)] flex-shrink-0"
              />
              <span className="text-[var(--color-text-secondary)]">
                Episode #{episode.episodeid}
              </span>
            </div>
          </div>

          {/* Description */}
          {(episode.description || show.longdescription) && (
            <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
              <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide mb-2">
                About
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                {episode.description || show.longdescription}
              </p>
            </div>
          )}

          {/* Powered by attribution */}
          <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
            <p className="text-[10px] text-[var(--color-text-muted)] text-center">
              Powered by{' '}
              <a
                href="https://autopod.xyz"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-primary)] hover:underline"
              >
                AutoPod
              </a>
            </p>
          </div>
        </div>
      </div>
      </FocusTrap>
    </div>
  );
}
